import React from 'react';
import { MenuItem, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { OnboardingStatus, onboardingStatus, sortStatus } from 'api/Responses/SatoResponses';
import { SortStatus } from 'api/Responses/SetResponses';
import DropDown from 'components/DropDown/DropDown';
import InputField from 'components/InputField';
import Page from 'components/Page';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { StatusTile } from 'components/StatusTile/StatusTile';
import { TableContainer } from 'components/Table/Table';
import { useLanguage } from 'context/AppContext';
import useSatoSets, { StatusFilter } from 'hooks/useSatoSets';
import * as Helpers from 'utils/helpers/SATO.helpers';
import * as Styles from './SATOSetsPage.styles';

const SATOSetsPage: React.FC = () => {
  const [language] = useLanguage();
  const { loading, filteredDrafts, filter, setFilter, searchQuery, setSearchQuery, sort, setSort } = useSatoSets();

  return (
    <Page>
      {loading ? (
        <PageSpinner />
      ) : (
        <Styles.Container>
          <Styles.ConfigContainer>
            <Styles.SearchContainer>
              <InputField
                variant="outlined"
                fullWidth
                placeholder={language.Sato.setsPending.searchPlaceholder}
                onChange={e => setSearchQuery(e.target.value as string)}
                value={searchQuery}
                type="text"
              />
            </Styles.SearchContainer>
            <Styles.FilterContainer>
              <Typography variant="h5" component="h2">
                {language.Sato.setsPending.filter}
              </Typography>
              <DropDown value={filter} variant="outlined" onChange={e => setFilter(e.target.value as StatusFilter)}>
                <MenuItem value={'all'}>{language.Sato.setsPending.all}</MenuItem>
                <MenuItem value={OnboardingStatus.DRAFT}>{language.Sato.setsPending.draft}</MenuItem>
                <MenuItem value={OnboardingStatus.PENDING}>{language.Sato.setsPending.pending}</MenuItem>
              </DropDown>
              <Typography variant="h5" component="h2">
                {language.Sato.setsPending.sort}
              </Typography>
              <DropDown value={sort} variant="outlined" onChange={e => setSort(e.target.value as SortStatus)}>
                <MenuItem value={SortStatus.NEWEST}>{sortStatus['newest']}</MenuItem>
                <MenuItem value={SortStatus.OLDEST}>{sortStatus['oldest']}</MenuItem>
              </DropDown>
            </Styles.FilterContainer>
          </Styles.ConfigContainer>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{language.Sato.setsPending.code}</TableCell>
                  <TableCell>{language.Sato.setsPending.name}</TableCell>
                  <TableCell align="center">{language.Sato.setsPending.status}</TableCell>
                  <TableCell>{language.Sato.setsPending.dateSubmitted}</TableCell>
                  <TableCell>{language.Sato.setsPending.author}</TableCell>
                  <TableCell>{language.Sato.setsPending.jme}</TableCell>
                  <TableCell>{language.Sato.setsPending.dateApproved}</TableCell>
                  <TableCell>{language.Sato.setsPending.resubmitted}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDrafts.map(draft => (
                  <TableRow key={draft.code}>
                    <TableCell>
                      <a href={`/sato/${draft.code}`}>{draft.display_code}</a>
                    </TableCell>
                    <TableCell>
                      <a href={`/sato/${draft.code}`}>{draft.name}</a>
                    </TableCell>
                    <TableCell align="center">
                      <a href={`/sato/${draft.code}`}>
                        {draft.status && (
                          <StatusTile status={onboardingStatus[draft.status]}>
                            {language.Sato.status[draft.status]}
                          </StatusTile>
                        )}
                      </a>
                    </TableCell>
                    <TableCell>
                      <a href={`/sato/${draft.code}`}>{Helpers.getDisplayedSubmissionDate(draft)}</a>
                    </TableCell>
                    <TableCell>
                      <a href={`/sato/${draft.code}`}>{draft.submitted_by}</a>
                    </TableCell>
                    <TableCell>
                      <a href={`/sato/${draft.code}`}>{draft.region}</a>
                    </TableCell>
                    <TableCell>
                      <a href={`/sato/${draft.code}`}>
                        {draft.approved_date && new Date(draft.approved_date).toLocaleString()}
                      </a>
                    </TableCell>
                    <TableCell>
                      <a href={`/sato/${draft.code}`}>
                        {Helpers.getIsResubmitted(draft) ? language.Sato.setsPending.yes : language.Sato.setsPending.no}
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Styles.Container>
      )}
    </Page>
  );
};

export default SATOSetsPage;
